import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { CreateUpdateHouseRuleForm } from '../CreateUpdateHouseRuleForm'
import { houseRuleCreateSchema } from '../../../../forms/schemas/house_rule_create'
import { NHouseRules } from '../../../../interfaces/services/house-rules.interfaces'
import { IHouseRuleModel } from '../../../../interfaces/models/house-rules.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { createHouseRule, updateHouseRule } from '../../../../api/house-rules'

interface CreateUpdateHouseRuleModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  houseRule?: IHouseRuleModel
  society: ISocietiesModel
}

export const CreateUpdateHouseRuleModal = ({
  houseRule,
  children,
  show,
  close,
  society,
}: CreateUpdateHouseRuleModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!houseRule

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateHouseRule.flashMessage.updateHouseRuleFailure'
      : 'createUpdateHouseRule.flashMessage.createHouseRuleFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateHouseRule.flashMessage.updateHouseRuleSuccess'
      : 'createUpdateHouseRule.flashMessage.createHouseRuleSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutateUpdateHouseRule, isPending: isUpdatingHouseRule } =
    useMutation({
      mutationFn: updateHouseRule,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: (error) => {
        showErrorMessage()
        captureException(error)
      },
    })

  const { mutate: mutateCreateHouseRule, isPending: isCreatingHouseRule } =
    useMutation({
      mutationFn: createHouseRule,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: (error) => {
        showErrorMessage()
        captureException(error)
      },
    })

  const onSubmit = async (
    data: InferType<typeof houseRuleCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutateUpdateHouseRule({
        id: houseRule._id,
        body: data as NHouseRules.NPatch.IRequestBody,
      })
    } else {
      mutateCreateHouseRule(data as NHouseRules.NCreate.IRequestBody)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isUpdatingHouseRule || isCreatingHouseRule

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateHouseRule.editHouseRuleTitle'
                  : 'createUpdateHouseRule.addNewHouseRuleTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateHouseRuleForm
                society={society}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                houseRule={houseRule}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
